import React, { useEffect, useState } from "react";
import Modal from "../../components/UI/Modal";
import Button from "../../components/UI/Button";
import ConfirmDialog from "../../components/UI/ConfirmDialog";
import { teamApi, uploadApi } from "../../api/client";
import toast from "react-hot-toast";
import { Edit2, Trash2, Plus } from "lucide-react";

const TeamManager = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  // form state
  const [name, setName] = useState("");
  const [position, setPosition] = useState("");
  const [bio, setBio] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [imagePublicId, setImagePublicId] = useState("");
  const [uploading, setUploading] = useState(false);

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const res = await teamApi.list();
      // API returns { members: [...], pagination: {...} }
      const payload = res.data;
      setMembers(Array.isArray(payload) ? payload : payload?.members || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load team members");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, []);

  const openAdd = () => {
    setEditing(null);
    setName("");
    setPosition("");
    setBio("");
    setImageUrl("");
    setImagePublicId("");
    setIsOpen(true);
  };

  const openEdit = (m) => {
    setEditing(m._id);
    setName(m.name || "");
    setPosition(m.position || "");
    setBio(m.bio || "");
    setImageUrl(m.imageUrl || "");
    setImagePublicId(m.imagePublicId || "");
    setIsOpen(true);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      setUploading(true);
      const res = await uploadApi.single(form);
      const { url, publicId } = res.data;
      setImageUrl(url);
      setImagePublicId(publicId || "");
      toast.success("Image uploaded");
    } catch (err) {
      console.error(err);
      toast.error("Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Name is required");

    const payload = { name, position, bio, imageUrl, imagePublicId };
    try {
      if (editing) {
        await teamApi.update(editing, payload);
        toast.success("Team member updated");
      } else {
        await teamApi.create(payload);
        toast.success("Team member created");
      }
      setIsOpen(false);
      fetchMembers();
    } catch (err) {
      console.error(err);
      toast.error("Failed to save team member");
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    setShowDelete(true);
  };

  const [showDelete, setShowDelete] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const confirmDelete = async () => {
    if (!deletingId) return;
    try {
      setDeleting(true);
      await teamApi.delete(deletingId);
      toast.success("Team member deleted");
      fetchMembers();
      setShowDelete(false);
      setDeletingId(null);
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete team member");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          Team
        </h2>
        <Button variant="primary" onClick={openAdd} icon={Plus}>
          Add Member
        </Button>
      </div>

      <div>
        {loading ? (
          <div className="text-center py-8">Loading team members...</div>
        ) : members.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No team members found.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {members.map((m) => (
              <div
                key={m._id}
                className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 flex flex-col"
              >
                {m.imageUrl && (
                  <img
                    src={m.imageUrl}
                    alt={m.name}
                    className="h-40 w-full object-cover rounded-md mb-3"
                  />
                )}
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    {m.name}
                  </h3>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {m.position}
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                    {m.bio}
                  </p>
                </div>
                <div className="mt-4 flex gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => openEdit(m)}
                    icon={Edit2}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleDelete(m._id)}
                    icon={Trash2}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={editing ? "Edit Member" : "Add Member"}
        size="md"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Name
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Position
            </label>
            <input
              value={position}
              onChange={(e) => setPosition(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Bio
            </label>
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              rows={4}
              className="mt-1 block w-full rounded-md border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Image
            </label>
            <div className="mt-2 flex items-center gap-4">
              <input type="file" accept="image/*" onChange={handleFileChange} />
              {uploading && (
                <div className="text-sm text-gray-500">Uploading...</div>
              )}
            </div>
            {imageUrl && (
              <img
                src={imageUrl}
                alt="preview"
                className="mt-3 h-32 object-cover rounded-md"
              />
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" loading={uploading}>
              {editing ? "Save" : "Create"}
            </Button>
          </div>
        </form>
      </Modal>
      <ConfirmDialog
        isOpen={showDelete}
        onClose={() => {
          setShowDelete(false);
          setDeletingId(null);
        }}
        title="Delete Team Member"
        description={
          members.find((m) => m._id === deletingId)
            ? `Are you sure you want to delete "${members.find((m) => m._id === deletingId).name}"? This action cannot be undone.`
            : "Are you sure you want to delete this team member? This action cannot be undone."
        }
        confirmLabel="Delete Member"
        onConfirm={confirmDelete}
        loading={deleting}
      />
    </div>
  );
};

export default TeamManager;
